import * as React from "react";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import { useI18n } from "../../i18n/I18nProvider";

type Props = {
  handlePageChange: (page: string) => void;
};

const stations = [
  {
    en: "Workforce Availability",
    de: "Mitarbeiterverfügbarkeit",
    questionEn: "How many of your employees would actually be able to work tomorrow? How do you know?",
    questionDe: "Wie viele Mitarbeitende könnten morgen tatsächlich arbeiten? Woher wisst ihr das?",
  },
  {
    en: "Critical Roles",
    de: "Kritische Rollen",
    questionEn: "Which ten functions must never fail in your company? Focus on functions, not names.",
    questionDe: "Welche zehn Funktionen dürfen in eurem Unternehmen niemals ausfallen? Denkt in Funktionen, nicht in Namen.",
  },
  {
    en: "Human Risk Map",
    de: "Human Risk Map",
    questionEn: "Which factors influence availability: family, caregiving, commuting, reservist, volunteering, home office, business travel, shift work?",
    questionDe: "Welche Faktoren beeinflussen Verfügbarkeit: Familie, Pflege, Pendeln, Reserve, Ehrenamt, Homeoffice, Dienstreisen, Schichtarbeit?",
  },
  {
    en: "Readiness Gap",
    de: "Readiness Gap",
    questionEn: "Which information is missing most today: availability, key roles, training status, preparedness level or measures?",
    questionDe: "Welche Information fehlt heute am meisten: Verfügbarkeit, Schlüsselrollen, Trainingsstand, Preparedness Level oder Maßnahmen?",
  },
  {
    en: "Business Impact",
    de: "Business Impact",
    questionEn: "What does a 30% workforce outage cost? Which areas are affected first? What decision would change with this information?",
    questionDe: "Was kostet ein 30%iger Personalausfall? Welche Bereiche sind zuerst betroffen? Welche Entscheidung würde sich mit dieser Information ändern?",
  },
];

export default function MeetingRoom({ handlePageChange }: Props) {
  const { lang } = useI18n();
  const isDE = lang === "de";

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 48px)",
        px: { xs: 2, sm: 3 },
        py: { xs: 7, sm: 8 },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundImage: `
          linear-gradient(rgba(8, 8, 8, 0.78), rgba(8, 8, 8, 0.86)),
          url("/scenarioBgs/communityPrep.png")
        `,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <Paper
        sx={{
          width: "min(94vw, 1040px)",
          borderRadius: 3,
          p: { xs: 3, sm: 5 },
          border: "1px solid rgba(255,255,255,0.14)",
          background: "linear-gradient(180deg, rgba(31,28,28,0.96), rgba(18,17,17,0.96))",
          boxShadow: "0 24px 65px rgba(0,0,0,0.45)",
        }}
      >
        <Stack spacing={4}>
          <Stack spacing={1.5} alignItems="center" textAlign="center">
            <GroupsOutlinedIcon sx={{ color: "#f05969", fontSize: 54 }} />
            <Typography
              sx={{
                color: "#fff",
                fontSize: { xs: "1.8rem", sm: "2.6rem" },
                fontWeight: 950,
                lineHeight: 1.12,
              }}
            >
              {isDE ? "Bitte weiter in den Meeting Room" : "Please continue to the Meeting Room"}
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.76)", maxWidth: 720, lineHeight: 1.65 }}>
              {isDE
                ? "Der individuelle Lab-Teil ist abgeschlossen. Die nächsten Fragen verbinden eure persönlichen Erkenntnisse mit der Führungs- und Unternehmensperspektive."
                : "The individual Lab journey is complete. The next questions connect your personal insight to the leadership and company conversation."}
            </Typography>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(5, 1fr)" },
              gap: 1.5,
            }}
          >
            {stations.map((station, index) => (
              <Box
                key={station.en}
                sx={{
                  minHeight: 220,
                  borderRadius: 2,
                  p: 2,
                  border: "1px solid rgba(255,255,255,0.14)",
                  backgroundColor: "rgba(255,255,255,0.06)",
                }}
              >
                <Typography sx={{ color: "#f05969", fontSize: 13, fontWeight: 900, mb: 1 }}>
                  {String(index + 1).padStart(2, "0")}
                </Typography>
                <Typography sx={{ color: "#fff", fontWeight: 950, mb: 1.25, lineHeight: 1.25 }}>
                  {isDE ? station.de : station.en}
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.76)", fontSize: 14, lineHeight: 1.55 }}>
                  {isDE ? station.questionDe : station.questionEn}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              variant="contained"
              onClick={() => handlePageChange("JourneyPillars")}
              sx={{
                borderRadius: 999,
                px: 4,
                py: 1.25,
                textTransform: "none",
                fontWeight: 900,
                background: "linear-gradient(90deg, #b50d20, #d6293e)",
                "&:hover": {
                  background: "linear-gradient(90deg, #9f0b1c, #c32136)",
                },
              }}
            >
              {isDE ? "Zurück zur Journey" : "Back to Journey"}
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}
